/**
 * Sales Guide knowledge API — /api/v1/sales-guide/knowledge (orchestration via gateway).
 */

import axios from "axios";
import { API_CONFIG, getAuthHeaders } from "../../../../_shared/lib/frontend/api/config";

const API_BASE = API_CONFIG.baseURL;

export const KNOWLEDGE_CATEGORIES = [
  "account_planning",
  "align_team",
  "book_meetings",
  "build_champions",
  "call_preparation",
  "closing",
  "cold_calling",
  "customer_success",
  "deal_hygiene_revops",
  "discovery",
  "economic_buyers",
  "enablement",
  "event_registrations",
  "follow_up",
  "get_contacts",
  "internal_reputation",
  "manager_alignment",
  "managing_procurement",
  "negotiation",
  "objection_handling",
  "onboarding_handoff",
  "outbound_email",
  "pilot_poc_trial",
  "post_sale",
  "prospecting",
  "qualification",
  "qualify_value",
  "reach_out",
  "territory_planning",
  "validate_tech",
  "working_with_delivery",
] as const;

export type KnowledgeCategory = (typeof KNOWLEDGE_CATEGORIES)[number];

export type KnowledgeSource = "standard" | "custom" | "override" | "brazn_standard";

export interface ContentStep {
  order: number;
  text: string;
}

export interface KnowledgeContent {
  when_to_use?: string;
  steps: ContentStep[];
  mistakes?: string;
  scripts?: string;
  summary?: string;
  /** Unsplash cover — seeded on platform standards; optional on tenant SOPs */
  cover_image_url?: string;
  cover_image_credit?: string;
  cover_image_credit_url?: string;
}

export interface KnowledgeEntry {
  id: string;
  title: string;
  content: KnowledgeContent;
  category: KnowledgeCategory;
  tags: string[];
  source: KnowledgeSource;
  layer: "standard" | "tenant";
  standard_id?: string | null;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface KnowledgeCreatePayload {
  title: string;
  content: KnowledgeContent;
  category: KnowledgeCategory;
  tags: string[];
}

export interface KnowledgeUpdatePayload {
  title?: string;
  content?: Partial<KnowledgeContent>;
  tags?: string[];
}

const BASE = `${API_BASE}/api/v1/sales-guide/knowledge`;

export const knowledgeApi = {
  list: async (category?: KnowledgeCategory): Promise<KnowledgeEntry[]> => {
    const response = await axios.get<KnowledgeEntry[]>(BASE, {
      params: category ? { category } : undefined,
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  get: async (id: string): Promise<KnowledgeEntry> => {
    const response = await axios.get<KnowledgeEntry>(`${BASE}/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  create: async (payload: KnowledgeCreatePayload): Promise<KnowledgeEntry> => {
    const response = await axios.post<KnowledgeEntry>(BASE, payload, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  update: async (id: string, payload: KnowledgeUpdatePayload): Promise<KnowledgeEntry> => {
    const response = await axios.patch<KnowledgeEntry>(`${BASE}/${id}`, payload, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await axios.delete(`${BASE}/${id}`, { headers: getAuthHeaders() });
  },

  clone: async (standardId: string): Promise<KnowledgeEntry> => {
    const response = await axios.post<KnowledgeEntry>(
      `${BASE}/${standardId}/clone`,
      {},
      { headers: getAuthHeaders() },
    );
    return response.data;
  },
};
